/**
 * The handle someone claimed from the landing hero.
 *
 * Written by `ClaimField` on the landing page and read by the register wizard's
 * handle step, which is why the name and the lifetime live here rather than in
 * either of them.
 *
 * A cookie rather than a query param. A handle in the URL survives being copied,
 * shared and indexed, and the sign-up flow can leave for an OAuth provider and
 * come back with a query string it did not write, which would drop it. Not
 * `httpOnly`: the field that writes it and the step that reads it are both
 * client code, and there is nothing secret in a name somebody has not claimed
 * yet.
 *
 * It is a hint, never an authority. The wizard re-checks the handle through the
 * same endpoint before offering it, because whatever this holds was true when
 * the visitor typed it and may not be by the time they finish signing up.
 */

export const CLAIM_COOKIE = 'fuse_claim'

/**
 * A day. Long enough to survive a sign-up interrupted by an email round trip or
 * an OAuth detour, short enough that a handle typed last week does not surprise
 * somebody returning to the page.
 */
export const CLAIM_COOKIE_MAX_AGE = 60 * 60 * 24

/** Reads the cookie. Client-only: the wizard's handle step is a Client Component. */
export function readClaim(): string {
  if(typeof document === 'undefined') return ''
  const match = document.cookie.match(new RegExp(`(?:^|; )${CLAIM_COOKIE}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : ''
}

/**
 * Clears it, once the handle has actually been claimed or deliberately
 * abandoned, so a later sign-up on the same device starts empty.
 */
export function clearClaim(): void {
  if(typeof document === 'undefined') return
  document.cookie = `${CLAIM_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`
}
